import { CreateUserParams, User, UserRecord } from "@nicsmith/shared_types";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export interface SessionState {
  message: string;
  user: User;
  idToken: string | null;
}

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_URL}/api/v1`,
  }),
  endpoints: (builder) => ({
    // User authentication (login or signup)
    authenticate: builder.mutation<SessionState, { idToken: string; preferred_language?: string }>({
      query: (payload) => ({
        url: "/auth/authenticate",
        method: "POST",
        body: payload,
      }),
    }),

    // User logout
    logout: builder.mutation<void, { idToken: string }>({
      query: (payload) => ({
        url: "/auth/logout",
        method: "POST",
        body: payload,
      }),
    }),

    signup: builder.mutation<UserRecord, CreateUserParams>({
      query: (payload) => ({
        url: "/auth/signup",
        method: "POST",
        body: payload,
      }),
    }),

    updatePassword: builder.mutation<
      void,
      { idToken: string; newPassword: string }
    >({
      query: (payload) => ({
        url: "/auth/update-password",
        method: "POST",
        body: payload,
      }),
    }),

    changePassword: builder.mutation<
      { message: string },
      { idToken: string; oldPassword: string; newPassword: string }
    >({
      query: (payload) => ({
        url: "/auth/change-password",
        method: "POST",
        body: payload,
      }),
    }),

    setPassword: builder.mutation<
      { message: string; auth_provider: string },
      { idToken: string; newPassword: string }
    >({
      query: (payload) => ({
        url: "/auth/set-password",
        method: "POST",
        body: payload,
      }),
    }),

    sendPasswordResetEmail: builder.mutation<
      void,
      { email: string; useCognito?: boolean }
    >({
      query: (payload) => ({
        url: "/auth/send-password-reset-email",
        method: "POST",
        body: payload,
      }),
    }),

    // Cognito-specific: Confirm password reset with code
    confirmPasswordReset: builder.mutation<
      void,
      { email: string; code: string; newPassword: string }
    >({
      query: (payload) => ({
        url: "/auth/confirm-password-reset",
        method: "POST",
        body: payload,
      }),
    }),

    validateInvite: builder.mutation<
      { valid: boolean; email: string; displayName?: string },
      { token: string }
    >({
      query: (payload) => ({
        url: "/auth/validate-invite",
        method: "POST",
        body: payload,
      }),
    }),

    acceptInvite: builder.mutation<
      { message: string },
      { token: string; password: string; displayName?: string }
    >({
      query: (payload) => ({
        url: "/auth/accept-invite",
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

export const {
  useAuthenticateMutation,
  useLogoutMutation,
  useSignupMutation,
  useUpdatePasswordMutation,
  useChangePasswordMutation,
  useSetPasswordMutation,
  useSendPasswordResetEmailMutation,
  useConfirmPasswordResetMutation,
  useValidateInviteMutation,
  useAcceptInviteMutation,
} = authApi;
